/**
 * QueueEmptyState component.
 *
 * Empty state component for queue views. Each empty state uses consistent
 * illustrated empty component with filter-specific messaging.
 */

import type { CSSProperties } from 'react'
import { NavLink } from 'react-router-dom'
import { Inbox, CheckCircle, History } from 'lucide-react'
import { Button, Card, CardContent } from '@/shared/components/ui'
import type { AttentionQueueTab, QueueView } from '../types'

export interface QueueEmptyStateProps {
  /** Current queue tab (determines message content). */
  tab: AttentionQueueTab
  /** Current `:view` URL segment — RETAINED FOR ROUTING BACK-COMPAT ONLY
   *  (Task 4072). The queue is one unified list, so the empty-state copy no
   *  longer varies by view; this is accepted but unused. Defaults to
   *  'attention'. */
  view?: QueueView
}

/**
 * Empty state component for the unified queue (Task 4072).
 *
 * The queue is a single unified list, so the empty-state copy is keyed on the
 * All/Mine/History tab only — it no longer varies by a (now-removed) top-level
 * Attention/Responses view.
 */
export function QueueEmptyState({ tab }: QueueEmptyStateProps) {
  const config = EMPTY_STATE_CONFIG[tab]
  const Icon = config.icon

  return (
    <Card>
      <CardContent style={styles.content}>
        <div style={styles.iconContainer}>
          <Icon style={styles.icon} />
        </div>
        <div>
          <p style={styles.title}>{config.title}</p>
          <p style={styles.description}>{config.description}</p>
        </div>
        {config.action && (
          <Button variant="secondary" asChild>
            <NavLink to={config.action.href}>{config.action.label}</NavLink>
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

const styles: Record<string, CSSProperties> = {
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '48px 24px',
    textAlign: 'center',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: 'var(--bg-tertiary)',
  },
  icon: {
    width: '32px',
    height: '32px',
    color: 'var(--text-muted)',
  },
  title: {
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  description: {
    marginTop: '4px',
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
}

interface EmptyStateConfig {
  icon: React.ElementType
  title: string
  description: string
  action?: {
    label: string
    href: string
  }
}

const EMPTY_STATE_CONFIG: Record<AttentionQueueTab, EmptyStateConfig> = {
  all: {
    icon: CheckCircle,
    title: 'No items requiring attention',
    description: 'The queue is clear. All agents are running autonomously.',
  },
  mine: {
    icon: Inbox,
    title: 'No items assigned to you',
    description: 'Assign items from the queue to see them here.',
    action: {
      label: 'View All',
      href: '/queue/attention/all',
    },
  },
  history: {
    icon: History,
    title: 'No resolved items match these filters',
    description: 'Try adjusting your filter criteria or time range.',
  },
}

export default QueueEmptyState
